import { Accessor, JSX, Setter, Show, children, createEffect, createRoot, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { createEventListener } from '@solid-primitives/event-listener';

import { cx, isSupportTouch } from '../utils';
import { useScrollElement } from '../components/Scrollview';
import createClickOutside from '../hooks/createClickOutside';
import { runClient } from '../components/ClientOnly';

/**
 * Creates a delayed signal update — typically for exit animations:
 * when a node is removed, run the animation first, then tear down.
 * @param value
 * @param getDelay
 * @returns
 */
function createDelaySignal<T>(value: Accessor<T>, getDelay: (value: T) => number) {
  const [delayValue, setDelayValue] = createSignal<T>(value());
  createEffect(() => {
    const data = value();
    const timeout = getDelay(data);
    if (timeout === 0) {
      setDelayValue(data as any);
    } else {
      const timer = setTimeout(() => setDelayValue(data as any), timeout);
      onCleanup(() => clearTimeout(timer));
    }
  });
  return delayValue;
}

interface TipData {
  node: HTMLDivElement;
  trigger: Element;
  padding?: number;
  position?: PositionType;
}

type PositionType = 'horizontal' | 'vertical';

const DELAY_TIME = 400;

// Global tooltip host element (singleton root)
let tip!: HTMLDivElement;

let cache: {
  value: Accessor<TipData | undefined>;
  node: JSX.Element;
  setValue: Setter<TipData | undefined>;
} =
  // eslint-disable-next-line
  createRoot(() => {
    let triangle!: HTMLDivElement;
    const [value, setValue] = createSignal<TipData>();
    const delayValue = createDelaySignal(value, (v) => (v ? 0 : DELAY_TIME));

    const forceUnmount = () => {
      setValue();
      if (document.body.contains(tip)) {
        document.body.removeChild(tip);
      }
    };

    // const child = children(() => delayValue()?.node);
    createEffect(() => {
      const data = delayValue();
      if (data && delayValue()?.node) {
        const rect = data.trigger.getBoundingClientRect();
        document.body.appendChild(tip);
        const padding = data.padding ?? 5;
        const position: PositionType = data.position ?? 'vertical';
        const tipRct = tip.getBoundingClientRect();
        let x = 0,
          y = 0,
          triangleX = 0,
          triangleY = 0;
        // console.log('position', position);
        if (position === 'horizontal') {
          triangleY = rect.y + rect.height / 2 - 1;
          y = Math.min(
            Math.max(padding, rect.y + (rect.height - tipRct.height) / 2),
            window.innerHeight - tipRct.height - padding
          );
          x = rect.x + rect.width + padding;
          // Show tip to the right of the trigger
          triangleX = x - triangle.clientWidth / 2 + 1;
          // Flip to the left if it would overflow the viewport
          if (x >= window.innerWidth) {
            x = rect.x - tipRct.width - padding;
            triangleX = x + tipRct.width - triangle.clientWidth / 2;
          }
        } else {
          triangleX = rect.x + rect.width / 2 - triangle.clientWidth / 2 + 1;
          x = Math.min(
            Math.max(padding, rect.x + (rect.width - tipRct.width) / 2),
            window.innerWidth - tipRct.width - padding
          );
          y = rect.y - (tipRct.height + 1 * padding);
          // Show tip above the trigger
          triangleY = y + tipRct.height;
          // Flip below if there is not enough space at the top
          if (y < padding) {
            y = rect.y + rect.height + padding;
            triangleY = y;
          }
        }
        tip.style.top = y + 'px';
        tip.style.left = x + 'px';
        tip.style.zIndex = '1001'; // z-[1001] on class did not apply reliably
        tip.style.setProperty('--triangle-left', triangleX + 'px');
        tip.style.setProperty('--triangle-top', triangleY - 1 + 'px');

        const handleTooltipEnter = () => setValue(data);
        const handleTooltipLeave = () => setValue();
        tip.addEventListener('mouseenter', handleTooltipEnter);
        tip.addEventListener('mouseleave', handleTooltipLeave);

        onCleanup(() => {
          tip.removeEventListener('mouseenter', handleTooltipEnter);
          tip.removeEventListener('mouseleave', handleTooltipLeave);
          if (document.body.contains(tip)) {
            tip.style.top = '';
            tip.style.left = '';
            tip.style.setProperty('--triangle-left', '');
            tip.style.setProperty('--triangle-top', '');
            document.body.removeChild(tip);
          }
        });
      } else {
        if (document.body.contains(tip)) {
          tip.style.top = '';
          tip.style.left = '';
          tip.style.setProperty('--triangle-left', '');
          tip.style.setProperty('--triangle-top', '');
          document.body.removeChild(tip);
        }
        return;
      }
    });
    return {
      value,
      node: (
        <Portal
          ref={(el) => {
            tip = el;
            el.className = 'tooltip';
            createClickOutside(
              () => (value()?.trigger ? [tip, value()!.trigger] : [tip]),
              () => {
                forceUnmount();
              }
            );
          }}
          children={
            <>
              <div ref={triangle} class="tooltip-triangle"></div>
              {delayValue()?.node}
            </>
          }
        />
      ),
      setValue,
    };
  });

interface TooltipProps {
  title?: JSX.Element;
  description?: JSX.Element;
  position?: PositionType;
}
export type TooltipArgs = string | JSX.Element | TooltipProps;
export function tooltip(element: Element, accessor: () => TooltipArgs): void {
  const [inited, setInitd] = createSignal(false);

  const [value, setValue] = [cache!.value, cache!.setValue];

  const node = children(
    runClient(() => {
      const props = createMemo(() => {
        const args = accessor();
        if (typeof args === 'object' && 'title' in (args as any) && !(args instanceof Element)) {
          return args as TooltipProps;
        }
        return {
          title: args,
        } as TooltipProps;
      });
      const title = createMemo(() => inited() && props()?.title);
      return (
        <Show when={title()}>
          <div class={cx(title() && props()!.description ? 'px-4 py-3 flex flex-col gap-y-1' : 'p-2')}>
            <span class={cx(props()!.description ? 'text-xs font-bold' : 'text-sm', 'text-primary')}>{title()}</span>
            <Show when={props()!.description}>
              <span class="text-xs text-secondary">{props()!.description}</span>
            </Show>
          </div>
        </Show>
      );
    })
  ) as Accessor<HTMLDivElement | undefined>;

  const onMouseEnter = () => {
    setInitd(true);
    const el = node();
    if (el) {
      setValue({
        trigger: element,
        node: el,
      });
    }
  };
  const onMouseLeave = () => {
    setValue();
  };
  const scrollElement = useScrollElement();

  createEventListener(
    () => scrollElement(),
    'scroll',
    () => {
      setValue();
      // forceUnmount();
    }
  );

  if (isSupportTouch) {
    // Mobile UX: tap to open, tap again to close
    createEventListener(element, 'touchstart', () => {
      if (value()) {
        setValue();
      } else {
        onMouseEnter();
      }
    });
  } else {
    createEventListener(element, 'mouseenter', onMouseEnter);
    createEventListener(element, 'mouseleave', onMouseLeave);
  }
}
